import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  AnalysisApiClientAccess,
  AppFeatureService,
  ApplicationApiClientAccess,
  AppRoleService,
  AppService,
} from '@nk/access-block';
import { CacheableObject, CachingService } from '@nk/caching-lib';
import { UPDATE_WATCHLIST_MAPPING_HANDLER_SERVICE_TOKEN, UpdateWatchlistHandlerService } from '@nk/company-block';
import {
  AppFeatureKey,
  AppIdentityCompositeEntity,
  AppRoleKey,
  CompanyBatchEntity,
  CompanyCollectionEntity,
  CompanyCustomerUploadMetaEntity,
  CompanyEntity,
  CompanyRiskScoreGroupingEntity,
  CompanyRiskScoreModelPriorityEntity,
  PartialCompanyByUuid,
  SimpleCollection,
  WatchlistAction,
  WatchlistChangeAction,
  WatchlistCompanyAggregateEntity,
  WatchlistCompanyRiskScoreMeasureAggregateEntity,
  WatchlistCompanyRiskScoreOfficialPrioritizedAggregateEntity,
  WatchlistCompositeEntity,
  WatchlistEntity,
  WatchlistMappingEntity,
} from '@nk/data-entities-lib';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { RiskService } from './risk.service';

@Injectable()
export class WatchlistService {
  constructor(
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _riskService: RiskService,
    private _cachingService: CachingService,
    private _appService: AppService,
    private _appRoleService: AppRoleService,
    private _appFeatureService: AppFeatureService,
    @Optional()
    @Inject(UPDATE_WATCHLIST_MAPPING_HANDLER_SERVICE_TOKEN)
    private _updateWatchlistHandlers: UpdateWatchlistHandlerService[],
  ) {}

  public registerWatchlistChange(watchlistChangeAction: WatchlistChangeAction): void {
    if (this._updateWatchlistHandlers) {
      this._updateWatchlistHandlers.map((handler) => {
        handler.registerWatchlistChange(watchlistChangeAction);
      });
    }
  }

  public watchlistByCurrentCustomerCache = this._cachingService.create(this, this.fetchWatchlistByCurrentCustomer);
  public fetchWatchlistByCurrentCustomer(): Observable<WatchlistEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      mergeMap((identity) => {
        return this.watchlistListByCustomerCache.get(identity.app_customer.uuid);
      }),
    );
  }

  public watchlistListByCustomerCache = this._cachingService.create(this, this.fetchWatchlistListByCustomer);
  public fetchWatchlistListByCustomer(customerUuid: string): Observable<WatchlistEntity[]> {
    return this._applicationApiAccess.get<WatchlistEntity[]>('app-customer/' + customerUuid + '/watchlist');
  }

  public watchlistByAppMembershipDigestSubscription = this._cachingService.create(
    this,
    this.fetchWatchlistByAppMembershipDigestSubscription,
  );
  public fetchWatchlistByAppMembershipDigestSubscription(appMembershipUuid: string): Observable<WatchlistEntity[]> {
    return this._applicationApiAccess.get<WatchlistEntity[]>(
      `app-membership/${appMembershipUuid}/digest-subscription/watchlist`,
    );
  }

  public watchlistRiskScoreMeasureByWatchlist(onlyShared = true): Observable<{
    watchlistAggregates: WatchlistCompanyRiskScoreMeasureAggregateEntity[][];
    identity: AppIdentityCompositeEntity;
    companyRiskScoreModelPriorityList: CompanyRiskScoreModelPriorityEntity[];
    relevantWatchlistList: WatchlistEntity[];
    allWatchlists: WatchlistEntity[];
  }> {
    let identity: AppIdentityCompositeEntity | null = null;
    let companyRiskScoreModelPriorityList: CompanyRiskScoreModelPriorityEntity[] | null = null;
    let watchlistList: WatchlistEntity[] | null = null;

    return this._appService.identityDataCache.get().pipe(
      mergeMap((identityPayload) => {
        identity = identityPayload;
        return this._riskService.companyRiskScoreModelPriorityListCache.getStableCache();
      }),
      tap((payload) => {
        // Sort riskModelPriority by ranking. Get the highest ranked model
        // Might not be necessary. Might already be sorted.
        companyRiskScoreModelPriorityList = payload.sort((a, b) => {
          return a.model_rank - b.model_rank;
        });
      }),
      mergeMap(() => this.watchlistListByCustomerCache.get(identity.app_customer.uuid)),
      mergeMap((payload) => {
        if (onlyShared) {
          watchlistList = payload.filter((watchlist) => !watchlist.personal_flag);
        }
        if (watchlistList.length > 0) {
          return forkJoin(
            watchlistList.map((w) =>
              this.watchlistCompanyRiskScoreMeasureAggregateByWatchlistCache.get(w.uuid).pipe(first()),
            ),
          );
        }
        return of([]);
      }),
      mergeMap((aggregateCollectionList: WatchlistCompanyRiskScoreMeasureAggregateEntity[][]) => {
        if (aggregateCollectionList.length > 0) {
          const prioritizedModel = companyRiskScoreModelPriorityList[0];
          let watchlistAggregates = aggregateCollectionList.map((wa, index) => {
            return wa.filter((a) => a.company_risk_score_model_uuid === prioritizedModel.company_risk_score_model_uuid);
          });

          watchlistAggregates = watchlistAggregates.filter((a) => a.length > 0);

          const watchlistWithAggregate = watchlistAggregates.map((a) => a[0].watchlist_uuid);
          const relevantWatchlistList = watchlistList.filter((a) => watchlistWithAggregate.indexOf(a.uuid) >= 0);

          return of({
            watchlistAggregates,
            identity,
            companyRiskScoreModelPriorityList,
            relevantWatchlistList,
            allWatchlists: watchlistList,
          });
        } else {
          return of({
            watchlistAggregates: [],
            identity,
            companyRiskScoreModelPriorityList,
            relevantWatchlistList: [],
            allWatchlists: watchlistList,
          });
        }
      }),
    );
  }

  public watchlistByUuidCache = this._cachingService.create(this, this.fetchWatchlistByUuid);
  public fetchWatchlistByUuid(watchlistUuid: string): Observable<WatchlistEntity> {
    return this._applicationApiAccess.get<WatchlistEntity>('/watchlist/' + watchlistUuid);
  }

  public createWatchlist(watchlist: WatchlistEntity): Observable<WatchlistEntity> {
    return this._applicationApiAccess.post<WatchlistEntity>('/watchlist', watchlist);
  }

  public updateWatchlist(watchlistUuid: string, watchlist: WatchlistEntity): Observable<WatchlistEntity> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._applicationApiAccess.put<WatchlistEntity>(
          `/watchlist/${watchlistUuid}`,
          watchlist,
          null,
          validFeatures,
          validRoles,
        );
      }),
    );
  }

  // TODO: need screening warning here.
  public deleteWatchlist(watchlistUuid: string): Observable<WatchlistEntity> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._applicationApiAccess.delete<WatchlistEntity>(
          `/watchlist/${watchlistUuid}`,
          null,
          validFeatures,
          validRoles,
        );
      }),
    );
  }

  private _postCompaniesToWatchlist(
    watchlistUuid: string,
    companies: Partial<CompanyEntity>[],
    returnResultsFlag = true,
  ): Observable<CompanyEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('return_results_flag', returnResultsFlag ? 'true' : 'false');
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._analysisApiAccess.post<CompanyEntity[]>(
          `/watchlist/${watchlistUuid}/company`,
          companies,
          { params: httpParams },
          validFeatures,
          validRoles,
        );
      }),
    );
  }

  // -- POST

  // Multiple
  public postCompaniesToWatchlistUsingCompanyBatch(
    watchlistUuid: string,
    companyBatchUuid: string,
    returnResultsFlag = false,
  ): Observable<CompanyEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_batch_identifier', companyBatchUuid);
    httpParams = httpParams.append('return_results_flag', String(returnResultsFlag));
    return this._applicationApiAccess
      .post<
        CompanyEntity[]
      >(`/watchlist/${watchlistUuid}/company`, null, { params: httpParams }, [AppFeatureKey.base], [AppRoleKey.superuser, AppRoleKey.customer_admin])
      .pipe(
        tap((a) => {
          if (returnResultsFlag && a.length > 0) {
            const watchlistChangeAction: WatchlistChangeAction = {
              type: WatchlistAction.PostMultipleMappings,
              payload: {
                watchlistUuid: watchlistUuid,
              },
            };
            this.registerWatchlistChange(watchlistChangeAction);
          }
        }),
      );
  }

  // Multiple
  public postCompaniesToWatchlist(
    watchlistUuid: string,
    companies: Partial<CompanyEntity>[],
  ): Observable<CompanyEntity[]> {
    return this._postCompaniesToWatchlist(watchlistUuid, companies).pipe(
      tap(() => {
        const watchlistChangeAction: WatchlistChangeAction = {
          type: WatchlistAction.PostMultipleMappings,
          payload: {
            watchlistUuid: watchlistUuid,
          },
        };
        this.registerWatchlistChange(watchlistChangeAction);
      }),
    );
  }

  // Single
  public postCompanyToWatchlist(
    watchlistUuid: string,
    company: PartialCompanyByUuid,
    returnResultsFlag = true,
  ): Observable<CompanyEntity> {
    return this._postCompaniesToWatchlist(watchlistUuid, [company], returnResultsFlag).pipe(
      tap(() => {
        const watchlistChangeAction: WatchlistChangeAction = {
          type: WatchlistAction.PostOneMapping,
          payload: {
            watchlistUuid: watchlistUuid,
            companyUuid: company.uuid,
          },
        };
        this.registerWatchlistChange(watchlistChangeAction);
      }),
      map((companies) => (returnResultsFlag && companies ? companies[0] : null)),
    );
  }

  // -- DELETE

  // Multiple
  public deleteWatchlistMappingByWatchlist(watchlistUuid: string): Observable<unknown> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._applicationApiAccess.delete<CompanyEntity[]>(
          `/watchlist/${watchlistUuid}/company`,
          null,
          validFeatures,
          validRoles,
        );
      }),
      tap(() => {
        const watchlistChangeAction: WatchlistChangeAction = {
          type: WatchlistAction.DeleteMultipleMappings,
          payload: {
            watchlistUuid: watchlistUuid,
          },
        };
        this.registerWatchlistChange(watchlistChangeAction);
      }),
    );
  }

  // Multiple
  public deleteCompaniesInWatchlistUsingCompanyBatch(
    watchlistUuid: string,
    companyBatchUuid: string,
  ): Observable<WatchlistMappingEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_batch_identifier', companyBatchUuid);
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._applicationApiAccess.delete<WatchlistMappingEntity[]>(
          `/watchlist/${watchlistUuid}/company`,
          { params: httpParams },
          validFeatures,
          validRoles,
        );
      }),
      tap(() => {
        const watchlistChangeAction: WatchlistChangeAction = {
          type: WatchlistAction.DeleteMultipleMappings,
          payload: {
            watchlistUuid: watchlistUuid,
          },
        };
        this.registerWatchlistChange(watchlistChangeAction);
      }),
    );
  }

  // Single
  public deleteWatchlistMappingByWatchlistAndCompany(
    watchlistUuid: string,
    companyUuid: string,
  ): Observable<CompanyEntity> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([AppFeatureKey.base]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.superuser, AppRoleKey.customer_admin]),
    ]).pipe(
      switchMap(([validFeatures, validRoles]) => {
        return this._applicationApiAccess.delete<CompanyEntity>(
          `/watchlist/${watchlistUuid}/company/${companyUuid}`,
          null,
          validFeatures,
          validRoles,
        );
      }),
      tap(() => {
        const watchlistChangeAction: WatchlistChangeAction = {
          type: WatchlistAction.DeleteOneMapping,
          payload: {
            watchlistUuid: watchlistUuid,
            companyUuid: companyUuid,
          },
        };
        this.registerWatchlistChange(watchlistChangeAction);
      }),
    );
  }

  // Warnings

  public getInsertWarningForWatchlist(watchlistUuid: string): Observable<string[]> {
    if (this._updateWatchlistHandlers && this._updateWatchlistHandlers.length) {
      return forkJoin(this._updateWatchlistHandlers.map((m) => m.addToWatchlistWarning(watchlistUuid)));
    }
    return of(null as null);
  }

  public getDeleteWarningForWatchlist(watchlist: WatchlistEntity): Observable<string[] | null> {
    if (this._updateWatchlistHandlers) {
      return forkJoin(this._updateWatchlistHandlers.map((m) => m.removeWatchlistWarning(watchlist)));
    }
    return of(null as null);
  }

  public getDeleteWarningsForWatchlistMapping(watchlistMapping: WatchlistMappingEntity): Observable<string[] | null> {
    if (this._updateWatchlistHandlers) {
      return forkJoin(this._updateWatchlistHandlers.map((m) => m.removeWatchlistMappingWarning(watchlistMapping)));
    }
    return of(null as null);
  }

  public fetchWatchlistsByCompanyAndCustomer(customerUuid: string, companyUuid: string): Observable<WatchlistEntity[]> {
    return this._applicationApiAccess.get<WatchlistEntity[]>(
      `/app-customer/${customerUuid}/company/${companyUuid}/watchlist`,
    );
  }

  public watchlistCompanyAggregateByWatchlistCache = this._cachingService.create(
    this,
    this.fetchWatchlistCompanyAggregateByWatchlist,
  );
  public fetchWatchlistCompanyAggregateByWatchlist(watchlistUuid: string): Observable<WatchlistCompanyAggregateEntity> {
    return this._applicationApiAccess
      .get<WatchlistCompanyAggregateEntity[]>(`/watchlist/${watchlistUuid}/watchlist-company-aggregate`)
      .pipe(map((data) => (data?.length ? data[0] : null)));
  }

  public companyCollectionByWatchlistCache = this._cachingService.create(this, this.fetchCompanyCollectionByWatchlist);
  public fetchCompanyCollectionByWatchlist(
    watchlistUuid: string,
    params: WatchlistCompanyListQueryParams, // TODO: These parameters are cached. We shouldn't pass complex objects to it. Convert to basic data types.
  ): Observable<CompanyCollectionEntity> {
    return this._applicationApiAccess.get<CompanyCollectionEntity>(`/watchlist/${watchlistUuid}/company-collection`, {
      params: params.createHttpParams(),
    });
  }

  public watchlistCompanyRiskScoreOfficialPrioritizedAggregateEntityByWatchlistCache = this._cachingService.create(
    this,
    this.fetchWatchlistCompanyRiskScoreMeasureAggregateEntityByWatchlistCacheByWatchlist,
  );
  public fetchWatchlistCompanyRiskScoreMeasureAggregateEntityByWatchlistCacheByWatchlist(
    watchlistUuid: string,
  ): Observable<WatchlistCompanyRiskScoreOfficialPrioritizedAggregateEntity[]> {
    return this._applicationApiAccess.get<WatchlistCompanyRiskScoreOfficialPrioritizedAggregateEntity[]>(
      `/watchlist/${watchlistUuid}/watchlist-company-risk-score-official-prioritized-latests-measure-aggregate`,
      {},
      [AppFeatureKey.base, AppFeatureKey.company_risk_score],
    );
  }

  public watchlistCompanyRiskScoreMeasureAggregateByWatchlistCache = this._cachingService.create(
    this,
    this.fetchWatchlistCompanyRiskScoreMeasureAggregateByWatchlist,
  );
  public fetchWatchlistCompanyRiskScoreMeasureAggregateByWatchlist(
    watchlistUuid: string,
    latestFlag: boolean = null,
    companyRiskScoreModel: string = null,
  ): Observable<WatchlistCompanyRiskScoreMeasureAggregateEntity[]> {
    let httpParams = new HttpParams();
    if (latestFlag !== null) {
      httpParams = httpParams.append('latest_flag', String(latestFlag));
    }
    if (companyRiskScoreModel) {
      httpParams = httpParams.append('company_risk_score_model_uuid', companyRiskScoreModel);
    }
    return this._applicationApiAccess.get<WatchlistCompanyRiskScoreMeasureAggregateEntity[]>(
      `/watchlist/${watchlistUuid}/watchlist-company-risk-score-measure-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.company_risk_score],
    );
  }

  public determineWatchlistCompanyRiskScoreGroupingAggregate(
    aggregate: WatchlistCompanyRiskScoreOfficialPrioritizedAggregateEntity,
  ): Observable<CompanyRiskScoreGroupingEntity> {
    return this._riskService.companyRiskScoreGroupingListCache.get().pipe(
      map((groupings) => {
        if (!aggregate || aggregate.measure_value_mean == null) {
          return groupings.find((g) => g.group_key === 'unset');
        } else {
          return groupings
            .filter((g) => g.group_key !== 'unset')
            .find((g) => {
              return (
                (g.from_measure_value == null && aggregate.measure_value_mean < g.to_measure_value) ||
                (g.from_measure_value <= aggregate.measure_value_mean &&
                  aggregate.measure_value_mean < g.to_measure_value) ||
                (g.from_measure_value <= aggregate.measure_value_mean && g.to_measure_value == null)
              );
            });
        }
      }),
    );
  }

  public fetchWatchlistCollectionComposite(
    appCustomerUuid: string,
    personalFlag: boolean,
    appMembershipUuid: string,
    excludedMembershipUuid: string,
  ): Observable<SimpleCollection<WatchlistCompositeEntity>> {
    let httpParams = new HttpParams();
    if (personalFlag !== undefined) {
      httpParams = httpParams.append('personal_flag', personalFlag);
    }
    if (appMembershipUuid !== undefined) {
      httpParams = httpParams.append('app_membership_uuid', appMembershipUuid);
    }
    if (excludedMembershipUuid !== undefined) {
      httpParams = httpParams.append('excluded_app_membership_uuid', excludedMembershipUuid);
    }
    return this._applicationApiAccess.get<SimpleCollection<WatchlistCompositeEntity>>(
      `/app-customer/${appCustomerUuid}/watchlist-composite-collection`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.company_risk_score],
    );
  }
}

export class WatchlistCompanyListQueryParams implements CacheableObject {
  constructor(
    public orderBy: string[],
    public orderByDesc: boolean[],
    public limit: number,
    public pageOffset: number,
    public companySearchTerm?: string,
  ) {}

  public createHttpParams(): HttpParams {
    // The HttpHeaders class is immutable, so every set() returns a new instance and applies the changes.
    // https://stackoverflow.com/questions/45374368/http-params-in-angular-4-3
    let httpParams = new HttpParams();
    httpParams = httpParams.append('order_by', this.orderBy.join(','));
    httpParams = httpParams.append('order_by_desc', this.orderByDesc.join(','));
    httpParams = httpParams.append('limit', this.limit.toString());
    httpParams = httpParams.append('offset', (this.pageOffset * this.limit).toString());
    if (this.companySearchTerm) {
      httpParams = httpParams.append('company_search_term', this.companySearchTerm);
    }
    return httpParams;
  }

  public toCachingKey(): string {
    return (
      this.orderBy.join('^^^') +
      '^^^' +
      this.orderByDesc.join('^^^') +
      '^^^' +
      this.limit +
      '^^^' +
      this.pageOffset +
      (this.companySearchTerm ? '^^^' + this.companySearchTerm : '')
    );
  }
}

export class WatchlistUploadFormData {
  public companyCustomerUploadMeta: CompanyCustomerUploadMetaEntity = null as CompanyCustomerUploadMetaEntity;
  public workSheetName = '';
  public workSheetOrgNrColumn = 'B';
  public worksheetOrgNrSchemaColumn = 'A';
  public countryMode = 'NO';
  public file = null as File;
  public companyBatch: CompanyBatchEntity;
  public companyBatchMappingCount: number;
}

export class WatchlistWizardFormData {
  public watchlistDetails = {
    name: null,
    personal: true,
  };
  public watchlistUpload = {
    companyCustomerUploadMeta: null as CompanyCustomerUploadMetaEntity,
    workSheetName: '',
    workSheetOrgNrColumn: '',
    file: undefined as File,
    companyBatch: undefined,
    companyBatchMappingCount: undefined as number,
  } as WatchlistUploadFormData;
  public watchlistCreationInProgress = false;

  constructor(public isPersonal = true) {
    if (typeof isPersonal !== 'undefined') {
      this.watchlistDetails.personal = isPersonal;
    }
  }
}

export interface WatchlistWizardStep {
  path: string;
  title: string;
  url: string;
  active: boolean;
  skip: boolean;
  required: boolean;
  nextActions: any[];
}
